import { IonIcon, IonLabel } from '@ionic/react'
import { barChart, helpCircle, trendingUp } from 'ionicons/icons'
import React, { FC, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../../analytics/utils/firebaseUtils'
import { useInfoBox } from '../../../../components/infoBox/useInfoBox'

interface QuarterHourlyToggleProps {
	infoText?: string
	showQuarterHourlyToggle?: boolean
	showQuarterHourly: boolean
	showBarGraph: boolean
	onChangeBarGraph: React.Dispatch<React.SetStateAction<boolean>>
	onChangeQuarterHourly: (value: boolean) => void
}

const QuarterHourlyToggle: FC<QuarterHourlyToggleProps> = ({
	showQuarterHourlyToggle = true,
	showQuarterHourly,
	onChangeQuarterHourly,
	showBarGraph,
	onChangeBarGraph,
	infoText = '',
}) => {
	const { t } = useTranslation('pages_electricity')

	const { open: openInfoBox, InfoBoxComponent } = useInfoBox<HTMLDivElement>()
	const infoAnchorRef = useRef<HTMLDivElement>(null)

	const activeStyle =
		'flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-400 rounded-md cursor-pointer border-[#52ae32] dark:border-[#3cb49f] border-2 rounded-md button text-brand-secondary-100-light dark:text-white dark:[&>ion-icon]:bg-brand-primary-100-dark [&>ion-icon]:bg-brand-primary-100-light [&>ion-icon]:text-white hover cursor-pointer select-none'
	const inactiveStyle =
		'flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-400 rounded-md cursor-pointer button text-[#707070] dark:text-brand-primary-100-dark dark:[&>ion-icon]:bg-[#144448] [&>ion-icon]:bg-[#e5e5e5] [&>ion-icon]:text-[#a3a3a3] dark:[&>ion-icon]:text-brand-primary-100-dark hover cursor-pointer select-none'

	const toggleQuarterHourly = () => {
		logEvent('click-toggle-quarter-hourly-data', { value: !showQuarterHourly })
		onChangeQuarterHourly(!showQuarterHourly)
	}

	const toggleGraphHourly = () => {
		logEvent('click-toggle-show-bar-graph', { value: !showBarGraph })
		onChangeBarGraph(!showBarGraph)
	}

	const onOpenInfo = () => {
		openInfoBox([
			{
				anchor: infoAnchorRef,
				content: (
					<div style={{ padding: '0.6em' }} key='infobox-1'>
						{infoText}
					</div>
				),
			},
		])
	}

	return (
		<div className='chart-buttons w-full flex flex-col gap-4 bg-white dark:bg-[#0d2d30]'>
			<div className='flex flex-row items-center justify-between pb-4'>
				<div
					className='pl-2 pr-2 hover:bg-gray-100 dark:hover:bg-gray-400 rounded-md cursor-pointer dark:text-white'
					onClick={toggleGraphHourly}
					role='button'
					tabIndex={0}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault()
							toggleGraphHourly()
						}
					}}
					aria-pressed={!showBarGraph}
					aria-label={showBarGraph ? barChart : trendingUp}
				>
					<div className='flex items-center justify-center'>
						<IonLabel className='flex flex-row items-center gap-1'>
							<IonIcon icon={!showBarGraph ? barChart : trendingUp} />
							{!showBarGraph
								? t('quarterHourlyToggle.barGraph')
								: t('quarterHourlyToggle.lineGraph')}
						</IonLabel>
					</div>
				</div>
				<div className='flex justify-end gap-2'>
					{showQuarterHourlyToggle && <>
						<div
							className={!showQuarterHourly ? activeStyle : inactiveStyle}
							onClick={toggleQuarterHourly}
							role='button'
							tabIndex={0}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									toggleQuarterHourly()
								}
							}}
							aria-pressed={!showQuarterHourly}
							aria-label={t('quarterHourlyToggle.60min')}
						>
							<IonLabel className='flex items-center justify-center'>
								{t('quarterHourlyToggle.60min')}
							</IonLabel>
						</div>
						<div
							ref={infoAnchorRef}
							className={showQuarterHourly ? activeStyle : inactiveStyle}
							onClick={toggleQuarterHourly}
							role='button'
							tabIndex={0}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									toggleQuarterHourly()
								}
							}}
							aria-pressed={showQuarterHourly}
							aria-label={t('quarterHourlyToggle.15min')}
						>
							<IonLabel className='flex items-center justify-center'>
								{t('quarterHourlyToggle.15min')}
							</IonLabel>
						</div>
					</>}
					<div>
						<IonIcon
							className='text-[#070707] dark:text-white button-hover !text-[30px]'
							icon={helpCircle}
							onClick={onOpenInfo}
						/>
						{InfoBoxComponent}
					</div>
				</div>
			</div>
		</div>
	)
}

export default QuarterHourlyToggle
